import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  CircularProgress,
  Typography,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  IconButton,
  Tooltip
} from '@material-ui/core';
import { makeStyles } from '@material-ui/styles';
import VisibilityIcon from '@material-ui/icons/Visibility';
import {
  candidateApplicationsSelector,
  applicationsLoadingSelector,
  candidateApplicationsFetchedSelector,
  fetchCandidateApplications
} from '../../redux/features/applications/applicationsSlice';
import usePageTitle from '../../hooks/usePageTitle';

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(4)
  },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: 400
  },
  emptyState: {
    padding: theme.spacing(8),
    textAlign: 'center',
    color: theme.palette.text.secondary
  },
  table: {
    minWidth: 650
  },
  tableHead: {
    backgroundColor: theme.palette.type === 'dark' 
      ? theme.palette.background.default 
      : theme.palette.grey[100]
  },
  tableHeaderCell: {
    fontWeight: 700,
    fontSize: '0.95rem',
    color: theme.palette.text.primary,
    backgroundColor: theme.palette.type === 'dark' 
      ? theme.palette.background.default 
      : theme.palette.grey[100],
    padding: theme.spacing(2),
    borderBottom: `2px solid ${theme.palette.divider}`
  },
  tableCell: {
    padding: theme.spacing(2),
    borderBottom: `1px solid ${theme.palette.divider}`
  },
  tableRow: {
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
      cursor: 'pointer'
    },
    '&:last-child td': {
      borderBottom: 0
    }
  },
  statusChip: {
    fontWeight: 600,
    textTransform: 'capitalize'
  },
  statusChipAccepted: {
    fontWeight: 600,
    textTransform: 'capitalize',
    backgroundColor: '#4caf50',
    color: '#fff'
  },
  statusChipRejected: {
    fontWeight: 600,
    textTransform: 'capitalize',
    backgroundColor: '#f44336',
    color: '#fff'
  }
}));

const STATUS_COLORS = {
  pending: 'default',
  reviewed: 'primary',
  accepted: 'default', // Will use custom class for green
  rejected: 'default' // Will use custom class for red
};

const STATUS_LABELS = {
  pending: 'Applied',
  reviewed: 'Reviewed',
  accepted: 'Accepted',
  rejected: 'Rejected'
};

const MyApplications = () => {
  const classes = useStyles();
  const dispatch = useDispatch();
  usePageTitle('My Applications');

  const applications = useSelector(candidateApplicationsSelector);
  const isLoading = useSelector(applicationsLoadingSelector);
  const fetched = useSelector(candidateApplicationsFetchedSelector);

  useEffect(() => {
    if (!fetched && !isLoading) {
      console.log('📋 [MyApplications] Fetching candidate applications');
      dispatch(fetchCandidateApplications());
    }
  }, [dispatch, fetched, isLoading]);

  if (isLoading) {
    return (
      <div className={classes.root}>
        <Card>
          <CardHeader title="My Applications" />
          <CardContent className={classes.loadingContainer}>
            <CircularProgress />
            <Typography variant="h6" color="textSecondary" style={{ marginLeft: 16 }}>
              Loading your applications...
            </Typography>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className={classes.root}>
      <Card>
        <CardHeader
          title="My Applications"
          subheader={`You have applied to ${applications.length} job${applications.length !== 1 ? 's' : ''}`}
        />
        <CardContent>
          {applications.length === 0 ? (
            <Box className={classes.emptyState}>
              <Typography variant="h5" gutterBottom>
                No applications yet
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Start applying to jobs and track your progress here. Good luck with your job search!
              </Typography>
            </Box>
          ) : (
            <Table className={classes.table}>
              <TableHead className={classes.tableHead}>
                <TableRow>
                  <TableCell className={classes.tableHeaderCell}>Job Title</TableCell>
                  <TableCell className={classes.tableHeaderCell}>Applied Date</TableCell>
                  <TableCell className={classes.tableHeaderCell}>Status</TableCell>
                  <TableCell className={classes.tableHeaderCell}>Last Updated</TableCell>
                  <TableCell className={classes.tableHeaderCell} align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {applications.map((application) => (
                  <TableRow key={application.id} className={classes.tableRow}>
                    <TableCell className={classes.tableCell}>
                      <Typography variant="body1" style={{ fontWeight: 600 }}>
                        {application.jobTitle || 'Job Title Not Available'}
                      </Typography>
                    </TableCell>
                    <TableCell className={classes.tableCell}>
                      {new Date(application.applicationDate).toLocaleDateString()}
                    </TableCell>
                    <TableCell className={classes.tableCell}>
                      <Chip
                        label={STATUS_LABELS[application.status] || application.status}
                        color={STATUS_COLORS[application.status] || 'default'}
                        size="small"
                        className={
                          application.status === 'accepted'
                            ? classes.statusChipAccepted
                            : application.status === 'rejected'
                            ? classes.statusChipRejected
                            : classes.statusChip
                        }
                      />
                    </TableCell>
                    <TableCell className={classes.tableCell}>
                      {new Date(application.updatedAt).toLocaleDateString()}
                    </TableCell>
                    <TableCell className={classes.tableCell} align="center">
                      <Tooltip title="View Job Details">
                        <IconButton
                          size="small"
                          component="a"
                          href={`/job/${application.jobId}`}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          <VisibilityIcon />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default MyApplications;
